import { useCallback, useEffect, useState } from 'react';
import { api } from '../api';
import { Link } from 'react-router-dom';
import { Card, todayIso, useToast } from '../components/ui';
import type { Member, PickupDay } from '../types';
import type { DashboardModuleProps } from './registry';

const T = {
  en: {
    title: 'Who is picking up the kids today',
    edit: 'Just for today',
    close: 'Done',
    nobody: 'Not set',
    na: 'N/A',
    notToday: 'No pickup today',
    at: 'at',
    from: 'from',
    usual: 'usual',
    by: 'Picked up by',
    time: 'Time',
    place: 'Place',
    someoneElse: 'Someone else…',
    noPickup: 'No pickup needed',
    save: 'Save',
    saved: 'Changed for today only — the usual week is untouched',
    noChildren: 'No children in the household.',
    justToday: 'just today',
    backToUsual: 'Back to usual',
    backTitle: 'Drop today’s change and go back to the usual week',
    restored: 'Back to the usual arrangement',
    setUsual: 'Set the usual week',
    editHelp: 'Changes here apply to today only. The usual week lives in Settings.',
  },
  fr: {
    title: 'Qui récupère les enfants aujourd’hui',
    edit: 'Juste pour aujourd’hui',
    close: 'Terminé',
    nobody: 'Non défini',
    na: 'N/A',
    notToday: 'Pas de sortie aujourd’hui',
    at: 'à',
    from: 'à',
    usual: 'habituel',
    by: 'Récupéré par',
    time: 'Heure',
    place: 'Lieu',
    someoneElse: 'Quelqu’un d’autre…',
    noPickup: 'Pas besoin',
    save: 'Enregistrer',
    saved: 'Modifié pour aujourd’hui seulement — la semaine habituelle est inchangée',
    noChildren: 'Aucun enfant dans le foyer.',
    justToday: 'aujourd’hui seulement',
    backToUsual: 'Revenir à l’habitude',
    backTitle: 'Annuler le changement du jour et revenir à la semaine habituelle',
    restored: 'Retour à l’arrangement habituel',
    setUsual: 'Régler la semaine habituelle',
    editHelp: 'Les changements ici ne valent que pour aujourd’hui. La semaine habituelle est dans les Réglages.',
  },
} as const;

/**
 * The school run (module #1 beyond meals).
 *
 * Deliberately readable from across the kitchen: one line per child saying who
 * is collecting them, when and from where — or that nobody needs to.
 */
export function SchoolRunModule({ language }: DashboardModuleProps) {
  const t = T[language];
  const toast = useToast();
  const date = todayIso();

  const [day, setDay] = useState<PickupDay | null>(null);
  const [members, setMembers] = useState<Member[]>([]);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    const [pickups, household] = await Promise.all([api.pickups(date), api.household()]);
    setDay(pickups);
    setMembers(household.members);
  }, [date]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!day) return null;
  if (day.entries.length === 0) return null;

  // Anyone who might reasonably do the school run.
  const candidates = members.filter((m) => m.member_type !== 'child').map((m) => m.name);

  /** Drops today's one-off so the standing weekly pattern applies again. */
  const revert = async (memberId: string) => {
    setBusy(true);
    try {
      setDay(await api.clearPickup(date, memberId));
      toast(t.restored);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not undo that');
    } finally {
      setBusy(false);
    }
  };

  const update = async (memberId: string, patch: Record<string, unknown>) => {
    setBusy(true);
    try {
      const entry = day.entries.find((e) => e.memberId === memberId)!;
      const next = await api.setPickup({
        date,
        memberId,
        pickedUpBy: entry.pickedUpBy,
        time: entry.time,
        place: entry.place,
        notRequired: entry.notRequired,
        ...patch,
      });
      setDay(next);
      toast(t.saved);
    } catch (error) {
      toast(error instanceof Error ? error.message : 'Could not save');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title={<h2>{t.title}</h2>}
      actions={
        <div className="row" style={{ gap: 8 }}>
          {editing && (
            <Link className="btn" to="/settings">
              {t.setUsual}
            </Link>
          )}
          <button className="btn" onClick={() => setEditing((v) => !v)}>
            {editing ? t.close : t.edit}
          </button>
        </div>
      }
      tight
    >
      {day.entries.map((entry) => (
        <div className="pickup-row" key={entry.memberId}>
          <span className="pickup-child">{entry.memberName}</span>

          {!editing ? (
            <span className={`pickup-detail${entry.notRequired ? ' na' : ''}`}>
              {entry.notRequired ? (
                t.notToday
              ) : entry.pickedUpBy ? (
                <>
                  <strong>{entry.pickedUpBy}</strong>
                  {entry.time ? ` · ${entry.time}` : ''}
                  {entry.place ? ` · ${entry.place}` : ''}
                  {entry.fromTemplate ? (
                    <span className="dim"> ({t.usual})</span>
                  ) : (
                    <span className="pill open" style={{ marginLeft: 8 }}>
                      {t.justToday}
                    </span>
                  )}
                </>
              ) : (
                <span className="dim">{t.nobody}</span>
              )}
            </span>
          ) : (
            <div className="pickup-edit">
              <select
                aria-label={t.by}
                value={entry.notRequired ? '__none__' : entry.pickedUpBy ?? ''}
                disabled={busy}
                onChange={(event) => {
                  const value = event.target.value;
                  if (value === '__none__') void update(entry.memberId, { notRequired: true, pickedUpBy: null });
                  else void update(entry.memberId, { notRequired: false, pickedUpBy: value || null });
                }}
              >
                <option value="">{t.nobody}</option>
                {candidates.map((name) => (
                  <option key={name} value={name}>
                    {name}
                  </option>
                ))}
                <option value="__none__">{t.noPickup}</option>
              </select>

              <input
                type="time"
                aria-label={t.time}
                placeholder={t.time}
                defaultValue={entry.time ?? ''}
                disabled={busy || entry.notRequired}
                onBlur={(event) =>
                  event.target.value !== (entry.time ?? '') &&
                  void update(entry.memberId, { time: event.target.value || null })
                }
              />
              <input
                type="text"
                aria-label={t.place}
                placeholder={t.place}
                defaultValue={entry.place ?? ''}
                disabled={busy || entry.notRequired}
                onBlur={(event) =>
                  event.target.value !== (entry.place ?? '') &&
                  void update(entry.memberId, { place: event.target.value || null })
                }
              />

              {!entry.fromTemplate && (
                <button
                  className="btn small"
                  onClick={() => void revert(entry.memberId)}
                  disabled={busy}
                  title={t.backTitle}
                >
                  ↩ {t.backToUsual}
                </button>
              )}
            </div>
          )}
        </div>
      ))}

      {editing && (
        <p className="dim" style={{ padding: '4px 18px 14px', fontSize: 13, margin: 0 }}>
          {t.editHelp}
        </p>
      )}
    </Card>
  );
}
